import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { fmtShortDate } from './constants';

export const SubmitDialog = ({ open, onOpenChange, stats, generalNotes, onSubmit, submitting }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [notes, setNotes] = useState(generalNotes || '');

  useEffect(() => {
    if (open) {
      setConfirmed(false);
      setNotes(generalNotes || '');
    }
  }, [open, generalNotes]);

  const incomplete = stats?.incomplete_dates || [];
  const canSubmit = incomplete.length === 0 && confirmed && !submitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="submit-dialog">
        <DialogHeader>
          <DialogTitle>Submit Availability</DialogTitle>
          <DialogDescription>Review your summary before submitting.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg border p-3">
              <div className="text-2xl font-bold text-[#1D9E75]" data-testid="submit-preferred-count">{stats?.preferred_count ?? 0}</div>
              <div className="text-[11px] text-muted-foreground">Preferred shifts</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-2xl font-bold text-[#3B82C4]" data-testid="submit-available-count">{stats?.available_count ?? 0}</div>
              <div className="text-[11px] text-muted-foreground">Available shifts</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-2xl font-bold text-[#D25454]" data-testid="submit-unavailable-count">{stats?.unavailable_days ?? 0}</div>
              <div className="text-[11px] text-muted-foreground">Unavailable days</div>
            </div>
          </div>

          {incomplete.length > 0 && (
            <div className="rounded-lg border border-[#D99E22] bg-[#FDF0D2]/60 p-3 text-sm" data-testid="submit-incomplete-warning">
              <div className="font-semibold text-[#8A6210] mb-1">
                {incomplete.length} date{incomplete.length === 1 ? '' : 's'} still need a response:
              </div>
              <div className="text-xs text-[#8A6210]">
                {incomplete.slice(0, 12).map(fmtShortDate).join(', ')}
                {incomplete.length > 12 ? ` +${incomplete.length - 12} more` : ''}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Scheduling notes for the full period <span className="text-muted-foreground font-normal">(optional)</span></label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder='e.g. "Prefer no more than three shifts per week", "Need alternating weekends"'
              rows={3}
              maxLength={1000}
              data-testid="submit-general-notes"
            />
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox checked={confirmed} onCheckedChange={setConfirmed} className="mt-0.5" data-testid="submit-confirm-checkbox" />
            <span>I confirm this availability is accurate and complete for the entire scheduling period.</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit(notes)} disabled={!canSubmit} data-testid="submit-confirm-button">
            {submitting ? 'Submitting…' : 'Submit Availability'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
